import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../api/axios';

interface TelegramAccount {
  _id: string;
  fullName: string;
  tgId: string;
}

const TelegramAccountsPage = () => {
  const [accounts, setAccounts] = useState<TelegramAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // поля для добавления
  const [newFullName, setNewFullName] = useState('');
  const [newTgId, setNewTgId] = useState('');

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/admin/telegram-accounts');
        setAccounts(response.data);
      } catch (err) {
        console.error(err);
        setError('Ошибка при загрузке аккаунтов.');
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const handleAddAccount = async () => {
    if (!newFullName || !newTgId) {
      setError('Введите ФИО и Telegram ID.');
      return;
    }

    try {
      const response = await api.post('/api/admin/telegram-accounts', {
        fullName: newFullName,
        tgId: newTgId,
      });

      setAccounts((prev) => [...prev, response.data]);
      setNewFullName('');
      setNewTgId('');
      setError('');
    } catch (err) {
      console.error(err);
      setError('Не удалось добавить аккаунт.');
    }
  };

  const handleUpdateAccount = async (id: string, field: keyof TelegramAccount, value: string) => {
    try {
      setAccounts((prev) =>
        prev.map((acc) => (acc._id === id ? { ...acc, [field]: value } : acc))
      );

      await api.put(`/api/admin/telegram-accounts/${id}`, {
        [field]: value,
      });
    } catch (err) {
      console.error(err);
      setError('Ошибка при обновлении аккаунта.');
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Удалить этот аккаунт?')) return;

    try {
      await api.delete(`/api/admin/telegram-accounts/${id}`);
      setAccounts((prev) => prev.filter((acc) => acc._id !== id));
    } catch (err) {
      console.error(err);
      setError('Не удалось удалить аккаунт.');
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h5" mb={3}>
        Реестр Telegram аккаунтов
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* форма добавления */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="ФИО"
          value={newFullName}
          onChange={(e) => setNewFullName(e.target.value)}
        />
        <TextField
          label="Telegram ID"
          value={newTgId}
          onChange={(e) => setNewTgId(e.target.value)}
        />
        <Button variant="contained" onClick={handleAddAccount}>
          Добавить
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : accounts.length === 0 ? (
        <Typography>Список аккаунтов пуст.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>ФИО</TableCell>
                <TableCell>Telegram ID</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((acc, index) => (
                <TableRow key={acc._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={acc.fullName}
                      onChange={(e) =>
                        handleUpdateAccount(acc._id, 'fullName', e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="standard"
                      value={acc.tgId}
                      onChange={(e) =>
                        handleUpdateAccount(acc._id, 'tgId', e.target.value)
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteAccount(acc._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TelegramAccountsPage;
